import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { PlainButton, space } from 'folio-common-components';
import * as React from 'react';
import type { MaAccountingSelection } from '../../../gqltypes';
import { SmallArrowLeftIcon } from '../../../icons';
import { setAccountingSelection } from '../../../services';
import { useActions, useSelector } from '../../../state';
import { isProd } from '../../../utils/is-prod';
import { Button, ButtonLink, TextButton } from '../../buttons';
import { StepHeading } from '../step-heading';

export const FikenStep: React.FC = () => {
  const info = useSelector(e => {
    const formOwner = e.editor.people[e.editor.formOwner];
    return {
      id: e.founding.id,
      bankSelection: e.founding.bankSelection,
      name: formOwner.name,
      email: formOwner.email,
      phone: formOwner.phone,
    };
  });
  const { foundingActions } = useActions();
  const [view, setView] = React.useState<'default' | 'youSure?'>('default');

  const applyAccountingSelection = async (selection: MaAccountingSelection) => {
    await setAccountingSelection({ id: info.id, selection });
    foundingActions.updateFounding({
      accountingSelection: selection,
    });

    // This will show another screen, but not change the URL
    // so we have to scroll manually.
    window.scrollTo(0, 0);
  };

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [view]);

  const fikenUrl = new URL(
    `/app/fra-stiftemaskinen/${info.id}`,
    isProd() ? 'https://fiken.no' : 'http://ci.fiken.no',
  );

  return view === 'default' ? (
    <>
      <StepHeading>Koble til Fiken</StepHeading>

      <p>
        Neste steg er å koble det nye selskapet til Fiken. I Fiken får du ferdig
        oppsatt regnskap, koblet mot banken din. Regnskap er lovpålagt for alle
        AS, og med Fiken gjør du det enkelt, helt selv. Så fort du har fått
        organisasjons&shy;nummer kan du begynne å føre kjøp og salg.
      </p>

      <p>
        Om du registrerer deg, bruker du Fiken gratis i 30 dager. Velger du å
        fortsette etter det, er prisen 199&nbsp;kr/mnd.
      </p>

      <div
        css={css`
          text-align: center;
        `}
      >
        <p>
          <ButtonLink
            href={fikenUrl.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            Prøv Fiken gratis
          </ButtonLink>
        </p>
        <TextButton onClick={() => setView('youSure?')}>
          Jeg har et annet regnskapssystem
        </TextButton>
      </div>
    </>
  ) : (
    <>
      <div
        css={css`
          ${space([32], 'margin-bottom')};
          color: var(--system-blue-700);
        `}
      >
        <PlainButton
          onClick={() => setView('default')}
          css={css`
            :hover {
              text-decoration: underline;
            }
          `}
        >
          <Arrow />
          Tilbake
        </PlainButton>
      </div>

      <StepHeading>Test enkelt regnskap med Fiken gratis!</StepHeading>

      <ul
        css={css`
          ${space([24], 'padding-left')};
          ${space([32], 'margin-bottom')};
        `}
      >
        <li>
          Laget for å være enkelt for deg som «ikke-økonom», med et språk du
          forstår
        </li>
        <li>
          Oversikt og påminnelser for frister – og enkel innsending til Altinn
        </li>
        <li>
          Med Folio som bank fikser vi automatisk vanskelige ting som
          åpnings&shy;balanse og aksje&shy;eierbok for deg
        </li>
        <li>Test selv helt gratis og uforpliktende!</li>
      </ul>

      <div
        css={css`
          display: grid;
          ${space([16], 'gap')};
          @media (min-width: 700px) {
            grid-template-columns: 1fr 1fr;
          }
        `}
      >
        <ButtonLink
          href={fikenUrl.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          Jeg vil prøve Fiken likevel
        </ButtonLink>
        <Button
          level="secondary"
          onClick={() => applyAccountingSelection('OTHER')}
        >
          Nei takk, jeg bruker et annet system
        </Button>
      </div>
    </>
  );
};

const Arrow = styled(SmallArrowLeftIcon)`
  ${space([8], 'margin-right')}
  position: relative;
  top: 1px;
`;
